import React, { FC, memo, ReactNode } from 'react'

import { useFormContext } from 'react-hook-form'

import { CURRENT_MILLENNIUM, railsContext } from 'Const'
import useEvent from 'Hooks/useEvent'

import SpreedlyPaymentForm, { SprErrorsCallback, SprTokenizeCreditCardFields } from 'Components/Spreedly'

import { usePayment } from '../../Provider'

interface Props {
  children: ReactNode
  onPaymentError(error?: string): void
  onPaymentReady(): void
}

const PaymentForm: FC<Props> = ({ children, onPaymentError, onPaymentReady }) => {
  const { checkout, setTokenizing } = usePayment()
  const { setValue, setError, clearErrors, getValues, trigger } = useFormContext()
  const environmentKey = 'spreedlyEnvironmentKey' in railsContext ? railsContext.spreedlyEnvironmentKey : ''

  const getTokenizeCreditCardFields = useEvent(() => {
    clearErrors(`spreedlyTokenize.number`)
    return new Promise<SprTokenizeCreditCardFields>((resolve, reject) => {
      trigger([
        'spreedlyTokenize.expiration',
        'spreedlyTokenize.zip',
        'spreedlyTokenize.first_name',
        'spreedlyTokenize.last_name',
      ])
        .then((valid) => {
          if (valid) {
            const { spreedlyTokenize } = getValues()
            const { expiration, ...rest } = spreedlyTokenize
            const [month, year] = expiration.split('/')
            resolve({ ...rest, month, year: CURRENT_MILLENNIUM + Number(year) })
          } else reject()
        })
        .catch(reject)
    })
  })

  const handleErrors = useEvent<SprErrorsCallback>((errors) => {
    setTokenizing(false)
    if (errors?.length) {
      console.error(errors)
      errors.forEach((error) => {
        if (error.attribute) setError(`spreedlyTokenize.${error.attribute}`, { message: error.message })
      })
    }
  })

  const handleSpreedlyTokenReceived = useEvent((token) => {
    setTokenizing(false)
    setValue('spreedlyPayment.sourceId', token)
    void checkout()
  })

  return (
    <SpreedlyPaymentForm
      environmentKey={environmentKey}
      getTokenizeCreditCardFields={getTokenizeCreditCardFields}
      paymentFormError={onPaymentError}
      paymentFormReady={onPaymentReady}
      tokenReceived={handleSpreedlyTokenReceived}
      onErrors={handleErrors}
    >
      {children}
    </SpreedlyPaymentForm>
  )
}

export default memo(PaymentForm)
